import Util from '../../util.js';

class Button extends HTMLElement {

    #template = document.createElement('template');
    $templateMarkup = `
        <button type="button">
            <slot></slot>
        </button>
    `;

    static BUTTON_TYPES = {
        primary: 'primary',
        secondary: 'secondary',
        tertiary: 'tertiary',
        flat: 'flat',
        destructive: 'destructive',
        destructiveOutline: 'destructive-outline'
    };

    static BUTTON_SIZES = {
        large: 'large',
        default: 'default',
        small: 'small',
        fullWidth: 'full-width'
    };

    static BUTTON_SHAPES = {
        round: 'round',
        square: 'square'
    };

    constructor() {
        super();
        this.attachShadow({mode: 'open'});
        this.addStyle = Util.addStyle;
        this.addStyle('/src/styles/neptune.css');
        this.addStyle('/src/components/atoms/button/button.css');
        this.#template.innerHTML = this.$templateMarkup;
        this.shadowRoot.appendChild(this.#template.content.cloneNode(true));
    }

    connectedCallback() {
        this.setButton();
    }

    static get observedAttributes() {
        return ['type', 'size', 'shape', 'left-icon', 'right-icon', 'disabled'];
    }

    setButton() {
        const buttonAttributes = {
            type: (this.getAttribute('type')  || '').trim().toLowerCase(),
            size: (this.getAttribute('size')  || '').trim().toLowerCase(),
            shape: (this.getAttribute('shape')  || '').trim().toLowerCase(),
            leftIcon: (this.getAttribute('left-icon')  || '').trim().toLowerCase(),
            rightIcon: (this.getAttribute('right-icon')  || '').trim().toLowerCase()
        };

        buttonAttributes.type = this.validateAttributes(Object.values(Button.BUTTON_TYPES), 'type', buttonAttributes.type);
        buttonAttributes.size = this.validateAttributes(Object.values(Button.BUTTON_SIZES), 'size', buttonAttributes.size);
        buttonAttributes.shape = this.validateAttributes(Object.values(Button.BUTTON_SHAPES), 'shape', buttonAttributes.shape);

        const buttonElement = this.shadowRoot.querySelector('button');

        const existingIcons = buttonElement.querySelectorAll('i');
        existingIcons.forEach(icon => icon.remove());

        if (buttonAttributes.leftIcon) {
            const leftIcon = this.#createIcon(buttonAttributes.leftIcon);
            buttonElement.insertBefore(leftIcon, buttonElement.querySelector('slot'));
        }

        if (buttonAttributes.rightIcon) {
            const rightIcon = this.#createIcon(buttonAttributes.rightIcon);
            buttonElement.appendChild(rightIcon);
        }

        this.updateButtonAttributes(buttonAttributes);
    }

    #createIcon(iconName) {
        const iconElement = document.createElement('i');
        iconElement.className = 'material-symbols-rounded';
        iconElement.textContent = iconName;
        return iconElement;
    }
    
    attributeChangedCallback(name, oldValue, newValue) {
        if(name === 'disabled') {
            const buttonElement = this.shadowRoot.querySelector('button');
            buttonElement.disabled = this.hasAttribute('disabled');

            if(buttonElement.disabled) {
                buttonElement.setAttribute('aria-disabled', 'true');
            } else {
                buttonElement.removeAttribute('aria-disabled');
            }
            return;
        }
        if(oldValue !== newValue && this.isConnected) {
            this.setButton();
        }
    }

    updateButtonAttributes(buttonAttributes) {
        if(this.getAttribute('type') !== buttonAttributes.type) {
            this.setAttribute('type', buttonAttributes.type);
        }
        if(this.getAttribute('size') !== buttonAttributes.size) {
            this.setAttribute('size', buttonAttributes.size);
        }
        if(this.getAttribute('shape') !== buttonAttributes.shape) {
            this.setAttribute('shape', buttonAttributes.shape);
        }
    }

    validateAttributes(validAttributes, attributeName, attributeValue) {
        if(!validAttributes.includes(attributeValue)) {
            console.error(`Invalid ${attributeName} value "${attributeValue}" for <neptune-button>. The valid attributes are: ${validAttributes.join(', ')}.`);
            throw new Error(`Invalid ${attributeName} value "${attributeValue}" for <neptune-button>. The valid attributes are: ${validAttributes.join(', ')}.`);
        }
        return attributeValue;
    }
}

customElements.define('neptune-button', Button);

export default Button;